import React, { useState, useEffect } from 'react';
import { Search, Printer, Loader2, FileText, Calendar, X, Clock } from 'lucide-react';
import { api, API_BASE } from '../utils/api';
import { formatDateThai, printReport } from '../utils/helpers';

const LateReport = ({ user }) => {
  const [summaryReports, setSummaryReports] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [dateRange, setDateRange] = useState({
    from: new Date().toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [printLoading, setPrintLoading] = useState(false);

  // Modal State
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailReport, setDetailReport] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadReports();
  }, [dateRange, selectedEmployee]);

  const loadInitialData = async () => {
    try {
      // ⚠️ ข้อมูลทั้งหมดนี้ จะถูก Backend กรองมาให้เฉพาะของโรงเรียนนี้แล้วอัตโนมัติ
      const [employeesData, organizationData] = await Promise.all([
        api.getEmployees(),
        api.getOrganizations()
      ]);
      
      const activeEmployees = employeesData.filter(emp => 
        emp.is_active == 1 && 
        emp.employee_id !== 'ADMIN' && 
        emp.position !== 'ผู้ดูแลระบบ'
      );
      
      setEmployees(activeEmployees);
      setOrganization(organizationData[0] || null);
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const params = {
        type: 'late',
        date_from: dateRange.from,
        date_to: dateRange.to
      };
      
      if (selectedEmployee) {
        params.employee_id = selectedEmployee;
      }

      // ⚠️ ดึงข้อมูลมาสายเฉพาะโรงเรียนตัวเอง
      let rawData = await api.getReports(params);
      
      rawData = rawData.filter(record => 
        record.employee_id !== 'ADMIN' && 
        record.status !== 'ไปราชการ' &&
        (!record.status || !record.status.startsWith('ลา'))
      );

      const groupedMap = {};
      
      rawData.forEach(record => {
          const empId = record.employee_id;
          if (!groupedMap[empId]) {
              groupedMap[empId] = {
                  employee_id: empId,
                  first_name: record.first_name,
                  last_name: record.last_name,
                  department: record.department,
                  total_times: 0,
                  records: []
              };
          }
          
          groupedMap[empId].total_times += 1;
          groupedMap[empId].records.push(record);
      });

      let summaryList = Object.values(groupedMap);
      
      summaryList.sort((a, b) => {
        const deptA = a.department || '';
        const deptB = b.department || '';
        if (deptA !== deptB) {
          return deptA.localeCompare(deptB, 'th');
        }
        return a.employee_id.localeCompare(b.employee_id);
      });
      
      setSummaryReports(summaryList);

    } catch (error) {
      console.error('Error loading reports:', error);
      setSummaryReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (summaryRecord) => {
      setDetailReport(summaryRecord);
      setIsDetailModalOpen(true);
  };

  // --- Print 1: Summary Report ---
  const generatePrintReport = () => {
    if (summaryReports.length === 0) {
      alert('ไม่มีข้อมูลสำหรับพิมพ์');
      return;
    }

    const dateTitle = (dateRange.from === dateRange.to 
        ? formatDateThai(dateRange.from)
        : `${formatDateThai(dateRange.from)} - ${formatDateThai(dateRange.to)}`);

    const logoHtml = organization?.logo 
      ? `<img src="${API_BASE}${organization.logo}" alt="โลโก้หน่วยงาน" class="logo">`
      : '';

    const reportTitle = `<br>รายงานสรุปสถิติการมาสาย ${dateTitle}`;
    
    // Header without Total Minutes
    const tableHeaders = `
      <tr>
        <th style="width: 10%;">ลำดับ</th>
        <th style="width: 35%;">ชื่อ-นามสกุล</th>
        <th style="width: 25%;">แผนก/กลุ่มสาระฯ</th>
        <th style="width: 10%;">จำนวน (ครั้ง)</th>
      </tr>
    `;
    
    const tableRows = summaryReports.map((item, index) => `
      <tr>
        <td style="text-align: center;">${index + 1}</td>
        <td>${item.first_name} ${item.last_name}</td>
        <td>${item.department || '-'}</td>
        <td style="text-align: center;">${item.total_times}</td>
      </tr>
    `).join('');

    const statHeadName = organization?.report_stat_head_name || '........................................';
    const statHeadPos = organization?.report_stat_head_position || 'เจ้าหน้าที่งานสถิติ';
    const vpName = organization?.report_vp_name || '........................................';
    const vpPos = organization?.report_vp_position || 'รองผู้อำนวยการกลุ่มบริหารงานบุคคล';

    const signatureSection = `
      <div class="signature-section" style="margin-top: 40px;">
        <table style="width: 100%; border: none; margin-bottom: 0;">
          <tr style="border: none; background-color: transparent;">
            <td style="border: none; text-align: center; width: 50%; vertical-align: top; padding: 0;">
              <p style="margin-bottom: 5px;">ลงชื่อ..........................................................</p>
              <p style="margin-bottom: 5px;">(${statHeadName})</p>
              <p>${statHeadPos}</p>
            </td>
            <td style="border: none; text-align: center; width: 50%; vertical-align: top; padding: 0;">
              <p style="margin-bottom: 5px;">ลงชื่อ..........................................................</p>
              <p style="margin-bottom: 5px;">(${vpName})</p>
              <p>${vpPos}</p>
            </td>
          </tr>
          <tr style="border: none; background-color: transparent;">
             <td colspan="2" style="border: none; text-align: center; padding-top: 20px;">
                วันที่ ${formatDateThai(new Date())}
             </td>
          </tr>
        </table>
      </div>
    `;

    const printStyles = getPrintStyles(); 

    const reportContent = `
      <style>${printStyles}</style>
      <div class="header">
        ${logoHtml}
        <div class="org-name">${organization?.name || 'หน่วยงาน'}</div>
        <div class="date">${reportTitle}</div>
      </div>
      <table>
        <thead>${tableHeaders}</thead>
        <tbody>${tableRows}</tbody>
      </table>
      ${signatureSection}
    `;

    printReport(reportContent, reportTitle);
  };

  // --- Print 2: Detail Report ---
  const generateDetailPrintReport = () => {
    if (!detailReport || detailReport.records.length === 0) {
      alert('ไม่มีข้อมูลสำหรับพิมพ์');
      return;
    }
    setPrintLoading(true);

    const emp = detailReport;
    const records = detailReport.records;
    
    records.sort((a, b) => new Date(a.work_date) - new Date(b.work_date));

    const dateTitle = (dateRange.from === dateRange.to 
        ? formatDateThai(dateRange.from)
        : `${formatDateThai(dateRange.from)} - ${formatDateThai(dateRange.to)}`);

    const logoHtml = organization?.logo 
      ? `<img src="${API_BASE}${organization.logo}" alt="โลโก้หน่วยงาน" class="logo">`
      : '';

    const reportTitle = `<br>รายงานรายละเอียดการมาสาย<br> ${emp.first_name} ${emp.last_name} (${emp.department || '-'})<br>ช่วงวันที่ ${dateTitle}`;

    // Removed Total Minutes from Summary
    const summaryHtml = `
      <div style="font-size: 18pt; margin-bottom: 15px;">
        รวมมาสาย: ${emp.total_times} ครั้ง
      </div>
    `;

    const tableHeaders = `
      <tr>
        <th style="width: 15%;">วันที่</th>
        <th style="width: 15%;">เวลาเข้า (กำหนด)</th>
        <th style="width: 15%;">เวลาเข้า (สแกน)</th>
        <th style="width: 45%;">หมายเหตุ</th>
      </tr>
    `;

    const tableRows = records.map(rec => `
      <tr>
        <td style="text-align: center;">${formatDateThai(rec.work_date)}</td>
        <td style="text-align: center;">${rec.work_start_time_snapshot || (organization?.work_start_time || '08:30').substring(0,5)}</td>
        <td style="text-align: center;">${rec.check_in || '-'}</td>
        <td>${rec.attendance_notes || '-'}</td>
      </tr>
    `).join('');

    const statHeadName = organization?.report_stat_head_name || '........................................';
    const statHeadPos = organization?.report_stat_head_position || 'เจ้าหน้าที่งานสถิติ';
    const vpName = organization?.report_vp_name || '........................................';
    const vpPos = organization?.report_vp_position || 'รองผู้อำนวยการกลุ่มบริหารงานบุคคล';

    const signatureSection = `
      <div class="signature-section" style="margin-top: 40px;">
        <table style="width: 100%; border: none; margin-bottom: 0;">
          <tr style="border: none; background-color: transparent;">
            <td style="border: none; text-align: center; width: 50%; vertical-align: top; padding: 0;">
              <p style="margin-bottom: 5px;">ลงชื่อ..........................................................</p>
              <p style="margin-bottom: 5px;">(${statHeadName})</p>
              <p>${statHeadPos}</p>
            </td>
            <td style="border: none; text-align: center; width: 50%; vertical-align: top; padding: 0;">
              <p style="margin-bottom: 5px;">ลงชื่อ..........................................................</p>
              <p style="margin-bottom: 5px;">(${vpName})</p>
              <p>${vpPos}</p>
            </td>
          </tr>
          <tr style="border: none; background-color: transparent;">
             <td colspan="2" style="border: none; text-align: center; padding-top: 20px;">
                วันที่ ${formatDateThai(new Date())}
             </td>
          </tr>
        </table>
      </div>
    `;

    const printStyles = getPrintStyles();

    const detailContent = `
      <style>${printStyles}</style>
      <div class="header">
        ${logoHtml}
        <div class="org-name">${organization?.name || 'หน่วยงาน'}</div>
        <div class="date">${reportTitle}</div>
      </div>
      ${summaryHtml}
      <table>
        <thead>${tableHeaders}</thead>
        <tbody>${tableRows}</tbody>
      </table>
      ${signatureSection}
    `;

    printReport(detailContent, reportTitle);
    setPrintLoading(false);
  };

  const getPrintStyles = () => `
    @font-face {
      font-family: 'Sarabun';
      src: url('${API_BASE}fonts/THSarabun.ttf') format('truetype');
      font-weight: 300;
      font-style: normal;
    }
    @font-face {
      font-family: 'Sarabun';
      src: url('${API_BASE}fonts/THSarabun-Bold.ttf') format('truetype');
      font-weight: 700;
      font-style: normal;
    }
    @page {
      size: A4 portrait; /* A4 แนวตั้ง */
      margin: 1cm;
    }
    body {
      font-family: 'Sarabun', sans-serif;
      font-size: 18pt; 
      color: #000;
    }
    .header {
      text-align: center;
      margin-bottom: 15px;
      border-bottom: 2px solid #000;
      padding-bottom: 10px;
      font-size: 18pt; 
    }
    .logo {
      max-width: 80px;
      max-height: 80px;
      margin: 0 auto 10px;
    }
    .org-name {
      font-weight: 700;
    }
    .date {
      font-weight: 700;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
    }
    th, td {
      border: 1px solid #000;
      padding: 6px 4px;   
      vertical-align: top;
      height: auto;       
      line-height: 1.3;
    }
    th {
      background-color: #f0f0f0;
      font-weight: 700;
      text-align: center;
    }
    td {
      text-align: left;
      word-break: break-word;
    }
    tr:nth-child(even) {
      background-color: #f9f9f9;
    }
    @media print {
      body { margin: 0; }
      .header {
        -webkit-print-color-adjust: exact;
        color-adjust: exact;
      }
    }
  `;

  return (
    <div className="space-y-6">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <h1 className="text-3xl font-bold text-gray-900">รายงานมาสาย</h1>
        <button
          onClick={generatePrintReport}
          disabled={loading || summaryReports.length === 0 || printLoading || !organization} 
          className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 disabled:opacity-50 flex items-center space-x-2 transition-colors"
        >
          {printLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
          <span>พิมพ์รายงานสรุป</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">จากวันที่</label>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({...dateRange, from: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ถึงวันที่</label>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({...dateRange, to: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">บุคลากร</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">ทุกคน</option>
              {employees
                .filter(emp => emp.employee_id !== 'ADMIN' && emp.position !== 'ผู้ดูแลระบบ')
                .map(emp => (
                  <option key={emp.employee_id} value={emp.employee_id}>
                    {emp.employee_id} - {emp.first_name} {emp.last_name}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>
              <p className="text-sm text-gray-500">กำลังโหลดข้อมูล...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">รหัส</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ชื่อ-นามสกุล</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">แผนก/กลุ่มสาระฯ</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">จำนวนครั้งที่สาย</th>
                    {/* Removed Total Minutes Column */}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {summaryReports.length > 0 ? summaryReports.map((record, index) => (
                    <tr 
                        key={`${record.employee_id}-${index}`} 
                        className="hover:bg-blue-50 cursor-pointer transition-colors"
                        onClick={() => handleRowClick(record)}
                        title="คลิกเพื่อดูรายละเอียด"
                    >
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.employee_id}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{record.first_name} {record.last_name}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{record.department || '-'}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">{record.total_times}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                        ไม่พบข้อมูลในช่วงที่เลือก
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {summaryReports.length > 0 && (
              <div className="mt-4 text-sm text-gray-600 flex justify-between items-center">
                <span>แสดงผลลัพธ์ {summaryReports.length} รายการ (คลิกที่แถวเพื่อดูรายละเอียด)</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* --- Detail Modal --- */}
      {isDetailModalOpen && detailReport && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl shadow-2xl max-h-[90vh] flex flex-col">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Clock className="h-6 w-6 text-red-600" />
                รายละเอียดการมาสาย: {detailReport.first_name} {detailReport.last_name}
              </h3>
              <button onClick={() => setIsDetailModalOpen(false)} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto space-y-4">
                
                <div className="bg-red-50 p-4 rounded-lg border border-red-200 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className='flex items-center gap-2 text-sm'>
                        <FileText className="h-4 w-4 text-red-600" />
                        <strong>รหัสพนักงาน:</strong> {detailReport.employee_id}
                    </div>
                    <div className='flex items-center gap-2 text-sm'>
                        <Calendar className="h-4 w-4 text-red-600" />
                        <strong>ช่วงรายงาน:</strong> {formatDateThai(dateRange.from)} - {formatDateThai(dateRange.to)}
                    </div>
                </div>

              {detailReport.records.length > 0 ? (
                <div className='overflow-x-auto'>
                    <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">วันที่</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เวลาเข้า (กำหนด)</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เวลาเข้า (สแกน)</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">สาย (นาที)</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">หมายเหตุ</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {detailReport.records
                                .sort((a,b) => new Date(a.work_date) - new Date(b.work_date))
                                .map((rec, idx) => (
                                <tr key={idx}>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{formatDateThai(rec.work_date)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                        {rec.work_start_time_snapshot || (organization?.work_start_time || '08:30').substring(0,5)}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{rec.check_in}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-red-600 text-center font-bold">{rec.late_minutes}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{rec.attendance_notes || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                    ไม่พบข้อมูล
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-lg flex justify-end gap-3">
              <button
                onClick={() => setIsDetailModalOpen(false)}
                className="px-4 py-2 rounded-md text-gray-700 font-medium hover:bg-gray-200 transition-colors"
              >
                ปิด
              </button>
              <button
                onClick={generateDetailPrintReport}
                disabled={detailReport.records.length === 0 || printLoading} 
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2 transition-colors"
              >
                {printLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                <span>พิมพ์รายงานละเอียด</span>
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default LateReport;